import { BigNumber } from '@frontend/numeric';

import { EInputType } from '@aqx-core/common/services/trade/analysis/equity-chart';

interface IDestructureAsReferenceBase {}

export const safetyCheckForNumberValues = (value?: number | null) => {
  return value === undefined || value === null || Number.isNaN(value) ? 0 : value;
};

export const numberForDisplay = (value: unknown, decimal?: number | null) => {
  return Number.isFinite(value) ? new BigNumber(value as number).toFormat(numberOrUndefined(decimal)) : '--';
};

export const numberOrUndefined = (value: unknown) => {
  return Number.isFinite(value) ? (value as number) : undefined;
};

export const numberOrDefault = (value: unknown, defaultValue = 0) => {
  return Number.isFinite(value) ? (value as number) : defaultValue;
};

export const numberAsBigNumberOrUndefined = (value: unknown) => {
  return Number.isFinite(value) ? new BigNumber(value as number) : undefined;
};

export const destructureAsBigNumber = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, BigNumber> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: new BigNumber(numberOrDefault(referenceObj?.[key], 0))
    }),
    {} as Record<K, BigNumber>
  );

export const destructureAsNumber = <T extends IDestructureAsReferenceBase, K extends keyof T>(
  referenceObj: T | undefined,
  keys: K[]
): Record<K, number> =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: numberOrDefault(referenceObj?.[key], 0)
    }),
    {} as Record<K, number>
  );

/**
 * To remove trailing zero of the number string , eg: 10.10 to 10.1
 * */
export const trimTrailingZero = (value: string | undefined): string => {
  if (!value) {
    return '';
  }
  // Remove commas from the string before parsing
  const cleanValue = value.replace(/,/g, '');
  if (isNaN(Number(cleanValue))) {
    return '';
  }
  return parseFloat(cleanValue).toString();
};

/**
 * Validates numeric values with optional negative sign and customizable decimal precision.
 *
 * Acceptable Values:
 * - Without options (default behavior):
 *   - 123
 *   - 1.23
 *   - -123
 *   - -1.23
 *
 * - With `allowNegative: true`:
 *   - -123
 *   - -1.23
 *
 * - With `maxDecimals: 0`:
 *   - 123
 *
 * - With `maxDecimals: 2`:
 *   - 123.45
 *   - -1.23
 *
 * - With both `allowNegative: true` and `maxDecimals: 3`:
 *   - -123.456
 *   - -1.234
 *   - 123.456
 *
 * Remarks:
 *  - This regex allowed values like 0. or .0 or .
 *  - Please handle it separately if your case doesn't accept above value.
 *
 * @param {Object} option - Options for validation.
 *   @param {boolean} option.allowNegative - (Optional) Allow negative values. Default is false.
 *   @param {number} option.maxDecimals - (Optional) Maximum number of decimal places. Default is unlimited. Pass 0 if don't want to accept decimal value.
 * @returns {RegExp} The regular expression for validating numeric values.
 */
export const numericRegex = (option?: { allowNegative?: boolean; maxDecimals?: number }) => {
  const { allowNegative, maxDecimals } = option || {};
  let decimalsPart: string;

  if (maxDecimals === undefined) {
    decimalsPart = `*`;
  } else if (maxDecimals === 0) {
    decimalsPart = '';
  } else {
    decimalsPart = `{0,${maxDecimals}}`;
  }
  return new RegExp(`^${allowNegative ? '-?' : ''}\\d*?${decimalsPart ? `\\.?\\d${decimalsPart}` : ''}$`);
};

export const mapZeroAndInvalidNumberToZero = (
  value: string,
  type: EInputType,
  onChange?: (value?: string | number) => void
) => {
  let inputValue = value;

  if (type === EInputType.NUMBER || type === EInputType.DECIMAL) {
    if (parseFloat(inputValue) === 0 || Number.isNaN(parseFloat(inputValue))) {
      inputValue = '1';
    } else if (inputValue.startsWith('.')) {
      inputValue = '0' + inputValue;
    } else if (inputValue.endsWith('.')) {
      inputValue = inputValue + '0';
    }
  }

  onChange?.(inputValue);

  return inputValue;
};
